import * as fetcher from "../fetcher";

export const fileTypeMap = new Map([
  ["png", "image/png"],
  ["jpg", "image/jpg"],
  ["jpeg", "image/jpeg"],
  ["pdf", "application/pdf"],
  ["xls", "application/vnd.ms-excel"],
  ["xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ["txt", "text/plain"],
  ["csv", "text/csv"],
  [
    "docx",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  ["eml", "message/rfc822"],
  ["msg", "application/vnd.ms-outlook"],
]);

export type Operation = "DOWNLOAD" | "OPEN" | "PREVIEW";

const downloadFile = async (
  opportunityId: string,
  fileName: string,
  attachmentId: number,
  operation: Operation,
  fileType?: string,
  fileUrl?: string
): Promise<Blob> => {
  const mimeType =
    fileTypeMap.get(fileType ?? "") ?? "application/octet-stream";

  const blob = fileUrl
    ? await fetchBlobFromUrl(fileUrl)
    : await fetchBlobFromOpportunity(opportunityId, attachmentId);

  triggerFileAction(blob, fileName, mimeType, operation);

  return blob;
};

const fetchBlobFromUrl = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file from URL: ${url}`);
  }
  return response.blob();
};

const fetchBlobFromOpportunity = async (
  opportunityId: string,
  attachmentId: number
): Promise<Blob> => {
  const response = await fetcher.opportunity.download(
    `/${opportunityId}/files/id/${attachmentId}`
  );
  if (!response) {
    throw new Error("No response from opportunity download");
  }
  return response;
};

const triggerFileAction = (
  blob: Blob,
  fileName: string,
  mimeType: string,
  operation: Operation
) => {
  const file = new File([blob], fileName, { type: mimeType });
  const url = URL.createObjectURL(file);

  if (operation === "DOWNLOAD") {
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    link.click();
    link.remove();
  } else if (operation === "OPEN") {
    window.open(url, "_blank")?.focus();
  }

  if (operation !== "PREVIEW") {
    URL.revokeObjectURL(url);
  }
};

const uploadFile = (opportunityId: string, file: File) => {
  return fetcher.opportunity.upload(`/${opportunityId}/files`, file);
};

const deleteFile = (opportunityId: string, attachmentId: number) => {
  return fetcher.opportunity.remove(
    `/${opportunityId}/files/id/${attachmentId}`
  );
};

const uploadSchedule = async (file: File) => {
  return await fetcher.core
    .upload(`/schedules/upload`, file)
    .then((res) => res.json());
};

export { downloadFile, uploadFile, deleteFile, uploadSchedule };
